/**
 * **旁路通道（side channel）—— 表现层的实时数据，不进状态、不经房主。**
 *
 * ## 它为什么必须存在
 *
 * 评审对"抽牌对峙"那套交互的要求里，有一条是关于**架构**的：
 *
 * > 调换手牌顺序等动作和 hover 的同步数据**不经过 host**，直接在各客户端间
 * > p2p 传输……不只是抽牌人面前会有一副牌，**其他人的客户端也会被动接受
 * > 二人的同步数据**（所以二人都是直接广播这些数据），他们也会看到二人
 * > 对着一手牌"斗智斗勇"，但不能造成任何干涉。
 *
 * 三条性质，缺一不可：
 *
 * | 性质 | 为什么 |
 * | --- | --- |
 * | **不走 RPC、不走房主** | 一次拖拽几十帧，走房主的请求/响应会把权威节点变成转播塔 |
 * | **广播给所有人** | 其他人要能看到这场对峙（纯观赏，不能干涉） |
 * | **不进 `GameState`** | 它是"手在动"，不是"局面的变化"。`architecture.md` §3.8 那条「不许往 `GameState` 里加时间字段」的直接推论 |
 *
 * ## 和 `hand/reorder` 的分工
 *
 * 这是这一节最容易搞混的地方，所以写清楚：
 *
 * | | `hand/reorder`（动作） | 旁路（这里） |
 * | --- | --- | --- |
 * | 什么时候用 | **热座**，以及任何"状态里就是真实顺序"的场合 | **联机**里的每一次理牌 / hover |
 * | 去哪 | `reduce` → `GameState` → 所有人的 `project()` | 直接广播给所有 peer |
 * | 带什么 | `{ actor, from, to }` | `{ from, to }` 或拖拽帧（**没有任何牌的 id**） |
 * | 为什么 | 单进程里它就是真相 | 联机时手牌顺序是**本机的事**，平时不发给任何人 |
 *
 * 所以"顺序会不会影响结果"那一刻（结算抽牌），房主才**主动索要**一次顺序 ——
 * 那一次走的是正常的请求/响应（`stealCard` 的 `order` 字段），
 * 而且会被校验是不是那一手牌的排列。
 *
 * ## 它不保证送达，也不需要
 *
 * 旁路数据是"手在动"的**采样**：丢一帧没有关系，下一帧会补上位置。
 * 所以它故意不做重传、不做确认 —— 那正是它和 `Rpc` 分开的理由。
 */

import { z } from 'zod';
import { t } from '../i18n';
import type { S3Channel } from './peer';
import type { Unsubscribe } from './transport';

/** 所有旁路方法名的前缀。**和 `s3/session/*` 并列**，不是一个 Session */
export const SIDE_PREFIX = 's3/side';

export function sideMethod(kind: string): string {
  if (kind.length === 0) throw new Error(`[s3] ${t('host.side.emptyKind')}`);
  return `${SIDE_PREFIX}/${kind}`;
}

export interface SideChannel {
  /**
   * 广播一条旁路数据。**给所有 peer，包括房主** —— 房主在这里只是
   * 另一个收件人，不做任何转发或裁决。
   */
  broadcast(kind: string, payload: unknown): void;
  /** 监听一种旁路数据。同一种可以有多个监听者 */
  on<T>(kind: string, schema: z.ZodType<T>, handler: (payload: T, from: string) => void): Unsubscribe;
}

export function createSideChannel(channel: S3Channel): SideChannel {
  return {
    broadcast(kind, payload) {
      // `notify(null, …)` = 广播。Trystero 会给**每条 DataChannel** 各发一份，
      // 所以它天然是"直连所有客户端"，没有中间人
      channel.notify(sideMethod(kind), payload);
    },
    on(kind, schema, handler) {
      return channel.onNotify(sideMethod(kind), schema, handler);
    },
  };
}

// ============================================================
// 抽牌对峙用的两种载荷
// ============================================================

/**
 * **一次拖拽的采样帧。**
 *
 * ⚠️ **没有任何牌的 id。** 只有"哪一位被拎起来了、拎到哪儿了"。
 * 所以被抽的人从头到尾没向任何人同步过自己的手牌 ——
 * 所有人看到的都只是**没有实际数据的牌背在动**（评审原话）。
 *
 * `dx` / `dy` 是相对**那一套扇形布局**的位移（设计像素），不是屏幕坐标：
 * 两边用同一个 `handLayout()` 算出来的弧心，所以对得上 ——
 * 这正是"精确到像素地重现拖拽"能成立的前提。
 */
export const DragFrameSchema = z.object({
  /** 被拎起来的那一位（0 = 最左）。**null = 松手了** */
  slot: z.number().int().nonnegative().nullable(),
  /**
   * **绝对位置**（相对扇形中心，单位 = 牌高），不是相对槽位的位移。
   *
   * ⚠️ 相对位移在拖动换位时会跳（`rest` 变了），旁路通道又是可丢包的，
   *    远端那张就会原地抽搐。绝对位置与"现在在哪一格"无关，丢一帧也只是
   *    停在原地、下一帧补上。
   */
  x: z.number(),
  y: z.number(),
  /**
   * 被拖那张的牌名（只有行动者知道）。带着它，翻面就不必依赖
   * `SIDE_REVEAL` 那一帧 —— 旁路通道可丢包，丢了就永远不翻。
   */
  title: z.string().nullable().optional(),
  /**
   * 松手会不会打出（越过出牌线）。观众据此决定要不要翻面 ——
   * 只是"按下"不翻，**拖到会打出的高度**才翻。旧端没有这一项时按 false。
   */
  armed: z.boolean().default(false),
});
export type DragFrame = z.infer<typeof DragFrameSchema>;

/**
 * **hover 了某一手牌里的第 i 张。**
 *
 * 用来传两类完全不同的 hover，**必须分开**，否则会串台：
 *
 * | 通道 | 语义 | 接收端 |
 * | --- | --- | --- |
 * | `SIDE_HOVER` | 行动者 hover **自己的手牌** | 观众顶部那根**旁观扇** |
 * | `SIDE_FAN_HOVER` | hover **被抽那人的手牌**（抽牌人 hover 取牌扇 / 被抽人 hover 自己手牌） | 对方那一侧对应的手牌／取牌扇 |
 *
 * `slot === null` = 移开了。
 */
export const HoverFrameSchema = z.object({
  slot: z.number().int().nonnegative().nullable(),
});
export type HoverFrame = z.infer<typeof HoverFrameSchema>;

/** 对峙的旁路方法名。收在这里，免得两边各拼一次字符串。
 *  （`SIDE_REORDER` 的定义在文件末尾，和它的 schema 放一起。） */
export const SIDE_DRAG = 'steal-drag';
export const SIDE_HOVER = 'steal-hover';
/** 取牌扇 ↔ 被抽人手牌的那条 hover 通道（见 `HoverFrameSchema`） */
export const SIDE_FAN_HOVER = 'steal-fan-hover';

/**
 * 采样节流。
 *
 * 拖动是逐帧的（60Hz），一次对峙几秒钟 = 几百条消息。人对"顺滑"的
 * 感知下限大约在 30Hz，所以按 `minIntervalMs` 丢帧就够了 ——
 * 反正丢掉的帧下一帧会补上位置（旁路通道不保证送达，也不需要）。
 *
 * ⚠️ **最后一个帧一定要发出去**（`flush`），否则松手的位置会停在半路。
 */
export function createThrottle(
  minIntervalMs: number,
  send: (payload: unknown) => void,
): { push(payload: unknown, nowMs: number): void; flush(payload: unknown): void } {
  let last = Number.NEGATIVE_INFINITY;
  return {
    push(payload, nowMs) {
      if (nowMs - last < minIntervalMs) return;
      last = nowMs;
      send(payload);
    },
    flush(payload) {
      last = Number.NEGATIVE_INFINITY;
      send(payload);
    },
  };
}
/**
 * **一次理牌的净移动**（从位次 `from` 挪到 `to`，0 = 最左）。
 *
 * 拖动期间发的是逐帧位移；松手时补这一条，接收端的牌背才会**真的重排**
 * （不然它只跟着位移、松手又弹回原位）。
 */
export const SIDE_REORDER = 'steal-reorder';
export const ReorderFrameSchema = z.object({
  from: z.number().int().nonnegative(),
  to: z.number().int().nonnegative(),
});
export type ReorderFrame = z.infer<typeof ReorderFrameSchema>;


/** actor 的手牌**数量**（只同步数量）。观众据它摆牌背扇 */
export const SIDE_HAND = 'turn-hand';
export const HandCountSchema = z.object({ count: z.number().int().nonnegative() });

/**
 * pointerdown 时**提前透露**的牌面；拖动期间沿用。
 * `slot: null` = 松开了，把翻面收回。
 */
export const SIDE_REVEAL = 'turn-reveal';
export const RevealFrameSchema = z.object({
  slot: z.number().int().nonnegative().nullable(),
  title: z.string().nullable(),
});
export type RevealFrame = z.infer<typeof RevealFrameSchema>;

/**
 * **[断点] 打断预示。**
 *
 * 正在拖 [断点] 的人广播自己的**行程** `progress`：0 = 刚按下、1 = 牌到了
 * 屏幕中心、>1 = 拖过头。只讲“拖了多远”，不讲牌 id、也不选择角度 ——
 * 入场角由每个接收端按同时活跃的发送者集合平均分配（`entryAngles`），
 * 于是所有观众看到同一套方向。
 *
 * 不进状态、不经房主：它只是“手在往屏幕中心拽”，不是局面的变化。
 */
export const SIDE_INTERRUPT = 'turn-interrupt';
export const InterruptFrameSchema = z.object({
  /** 行程。非负有限数；释放 / 取消发 0 */
  progress: z.number().nonnegative(),
});
export type InterruptFrame = z.infer<typeof InterruptFrameSchema>;

